import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Breadcrumbs, CircularProgress, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { filtroDataInteracao } from '../../components/propostasList';
import { MdiIcon, Table, WaitDialog } from '@components';
import { mdiMagnify, mdiSync } from '@mdi/js';
import moment from 'moment';
import { Button } from '@mui/material';
import { useModal } from 'mui-modal-provider';
const CrmPropostasListView = ({ $state, CrmService, UIService }) => {
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const ultimoPedido = useRef(0); // só a resposta do pedido mais recente chega à tabela (o filtro pode mudar a meio)
    const [syncing, setSyncing] = useState(false);
    const [filter, setFilter] = useState([]); // filtros a aplicar na tabela
    const [sort, setSort] = useState([]); // ordenação da tabela
    const [options, setOptions] = useState({
        filter: [], // exemplo -> [{name: 'nome', type: 'TEXT'}, {name: 'ultimoContacto', type: 'DATE', options: {format: 'YYYY-MM-DD'}}, ...]
        sort: [] // exemplo -> ['id', 'nome', 'ultimoContacto', 'contactoTelefonico', 'nif', 'email', 'morada', 'localidade']
    });
    const { showModal } = useModal();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10
    });
    const atualizarPropostas = async () => {
        try {
            setSyncing(true);
            const res = await CrmService.allFromPrimavera();
            UIService.addToast(`Atualização concluída: ${res.importadas > 0 ? `${res.importadas} propostas novas` : 'sem propostas novas'}${res.erros > 0 ? `, ${res.erros} com erro` : ''}`);
            fetchData();
        }
        catch (e) {
            UIService.addToast(e?.data?.error?.message || 'Ocorreu um erro ao atualizar as propostas!');
        }
        finally {
            setSyncing(false);
        }
    };
    const initialFetch = async () => {
        if (loading) {
            // opt -> vai buscar os campos filtraveis
            const opt = await CrmService.optionsPropostas();
            setOptions(opt);
            setLoading(false);
        }
    };
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const getEstado = (estado) => {
        if (!estado)
            return '';
        if (estado.fechado)
            return 'Fechada';
        if (estado.anulado)
            return 'Anulada';
        switch (estado.estado) {
            case 'G':
                return 'Em Apreciação';
            case 'P':
                return 'Pendente';
            case 'S':
                return 'Substituída';
            case 'R':
                return 'Rejeitada';
            case 'T':
                return 'Transformada';
            default:
                return '';
        }
    };
    const fetchData = async () => {
        const pedido = ++ultimoPedido.current;
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
                if (r.id === 'data' && r.type === 'equals')
                    filters[r.id] = `$sw:${moment(r.value).format('YYYY-MM-DD')}`;
                filters[r.id] = filtroDataInteracao(r) ?? filters[r.id];
            });
            // Força a trazer apenas documentos que começam por "PR"
            filters.refPropostaPrimavera = '$ilike:PR';
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const aux = await CrmService.findAllPropostas({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            if (pedido !== ultimoPedido.current)
                return;
            // Update URL
            $state.go($state.current.name || '', {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            setData(aux);
        }
        catch (e) { }
        if (pedido === ultimoPedido.current)
            setRefetching(false);
    };
    const excelGen = async (id, array) => {
        const wait = showModal(WaitDialog, {
            title: 'Por favor aguarde...'
        });
        let filename = `crm-propostas-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
        let filters = {};
        filter.forEach(r => {
            filters[r.id] = filtroDataInteracao(r) ?? `${getType(r.type)}:${r.value}`;
        });
        if (id && array.length > 0) {
            filters['id'] = `$in:${array.join(',')}`;
        }
        let sorter = [];
        sort.forEach(r => {
            sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
        });
        try {
            const o = await CrmService.exportPropostas({
                columns: columns.map((r) => ({
                    accessorKey: r.exportKey ?? r.accessorKey,
                    header: r.header
                })),
                filter: filters,
                sortBy: sorter
            });
            if (o) {
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
            }
        }
        catch (e) {
            console.log(e);
            if (e?.data?.message) {
                UIService.addToast(e.data.message);
            }
            else {
                UIService.addToast('Ocorreu um erro ao exportar dados!');
            }
        }
        wait.destroy();
    };
    useEffect(() => {
        initialFetch();
        setRefetching(true);
        fetchData();
    }, [pagination, filter, sort]);
    const [data, setData] = useState();
    const columns = useMemo(() => [
        {
            accessorKey: 'refPropostaPrimavera',
            header: 'Número Documento',
            size: 150
        },
        {
            accessorKey: 'data',
            header: 'Data',
            size: 150,
            Cell: ({ row }) => (moment(row.original.data).isValid() ? moment(row.original.data).format('DD/MM/YYYY') : 'N/D')
        },
        {
            accessorKey: 'nome',
            header: 'Designação',
            size: 220
        },
        {
            accessorKey: 'precoTotal',
            header: 'Preço',
            size: 100,
            muiTableBodyCellProps: {
                align: 'right'
            },
            Cell: ({ row }) => (row.original.precoTotal != null ? row.original.precoTotal.toFixed(2).replace('.', ',') + ' €' : '-')
        },
        {
            accessorKey: 'estadoPrimavera',
            header: 'Estado',
            Cell: ({ row }) => getEstado(row.original.estadoPrimavera)
        },
        {
            accessorKey: 'ultimoFuncionario',
            header: 'Responsável Atual'
        },
        {
            accessorKey: 'dataUltimaInteracao',
            header: 'Última Interação',
            sortDescFirst: false, // a 1.ª linha pode vir vazia e o MRT deduziria a ordem a partir dela
            Cell: ({ renderedCellValue }) => (renderedCellValue ? moment(renderedCellValue).format('DD/MM/YYYY HH:mm') : '-')
        },
        {
            accessorKey: 'dataProximaInteracao',
            header: 'Próxima Interação',
            sortDescFirst: false, // a 1.ª linha pode vir vazia e o MRT deduziria a ordem a partir dela
            Cell: ({ renderedCellValue }) => (renderedCellValue ? moment(renderedCellValue).format('DD/MM/YYYY HH:mm') : '-')
        }
    ], []);
    return (<Box sx={{ mt: 2, pr: 1, pl: 1 }}>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link underline='none' color='inherit'>
          CRM
        </Link>

        <Link underline='hover' color='text.primary' aria-current='page'>
          Propostas
        </Link>
      </Breadcrumbs>

      <Stack direction='row' alignItems='center' sx={{ mt: 2 }}>
        <Typography variant='h4' sx={{ color: 'black' }}>
          Propostas
        </Typography>
        <Tooltip title='Atualizar propostas do Primavera' placement='top'>
          <span>
            <IconButton color='primary' disabled={syncing} onClick={atualizarPropostas}>
              {syncing ? <CircularProgress size={20}/> : <MdiIcon path={mdiSync} sx={{ color: '#1976D2' }}/>}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{
            borderRadius: '15px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
        <Table storageKey={$state.current.name || ''} data={data} columns={columns} initial={{
            sort: sort,
            filter: filter
        }} onExport={() => {
            excelGen(false, []);
        }} selectActions={selected => {
            return [
                <Button key='export' onClick={() => {
                        if (selected.length > 0) {
                            excelGen(true, selected);
                        }
                    }}>
                Exportar
              </Button>
            ];
        }} selectable={() => true} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} rowActionSize={70} onDoubleClick={row => {
            window.open($state.href('app.crm.propostas.details', { id: row.id }, { absolute: true }));
        }} actionItems={({ row }) => [
            <Box key={`actions-${row.original.id}`} gap={1} sx={{
                    width: '100%', // Ocupa toda a largura da célula
                    height: '100%', // Ocupa toda a altura da célula
                    display: 'flex',
                    justifyContent: 'center', // Centra horizontalmente
                    alignItems: 'center' // Centra verticalmente
                }}>
              <Tooltip title={'Ver detalhes'} placement='top'>
                <IconButton color='secondary' onClick={() => {
                    window.open($state.href('app.crm.propostas.details', { id: row.id }, { absolute: true }));
                }}>
                  <MdiIcon path={mdiMagnify} sx={{ color: '#1976D2' }}/>
                </IconButton>
              </Tooltip>
            </Box>
        ]}></Table>
      </Box>
    </Box>);
};
export default CrmPropostasListView;
